import { getAuthHeaders, isAuthenticated } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.centroautomotrizhlfrd.com.do/api';

const redirectToLogin = () => {
  localStorage.removeItem('client-auth-storage');
  window.location.href = '/login';
};

const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
  if (!isAuthenticated()) {
    redirectToLogin();
    throw new Error('No hay sesión activa');
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  const response = await fetch(url, config);

  if (response.status === 401) {
    redirectToLogin();
    throw new Error('Sesión expirada');
  }

  return response;
};

export interface QuickBookingData {
  serviceId: string;
  date: string;
  time: string;
  vehicleId: string;
  notes?: string;
}

export interface QuickBookingResponse {
  success: boolean;
  appointmentId?: string;
  message: string;
}

export interface AvailabilityResponse {
  available: boolean;
  reason?: string;
}

/**
 * Crear una reserva rápida
 */
export const createQuickBooking = async (bookingData: QuickBookingData): Promise<QuickBookingResponse> => {
  try {
    // Ensure time is in HH:MM format for the server
    const cleanTime = bookingData.time.split(':').slice(0, 2).join(':');
    const appointmentDateTime = `${bookingData.date}T${cleanTime}:00`;
    const response = await apiRequest(`${API_BASE_URL}/client/appointments`, {
      method: 'POST',
      body: JSON.stringify({
        serviceIds: [bookingData.serviceId],
        appointmentDate: appointmentDateTime,
        vehicleId: bookingData.vehicleId,
        notes: bookingData.notes || ''
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Error al crear la reserva');
    }

    const result = await response.json();
    return {
      success: true,
      appointmentId: result.appointmentId,
      message: 'Reserva creada exitosamente'
    };
  } catch (error) {
    console.error('Error creating quick booking:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Error desconocido al crear la reserva'
    };
  }
};

/**
 * Validar disponibilidad de horario
 */
export const checkAvailability = async (date: string, time: string): Promise<boolean> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/availability`, {
      method: 'POST',
      body: JSON.stringify({ date, time })
    });

    if (!response.ok) {
      return false;
    }

    const result: AvailabilityResponse = await response.json();
    return result.available;
  } catch (error) {
    console.error('Error checking availability:', error);
    return false;
  }
};

export const checkDateAvailability = async (date: string): Promise<AvailabilityResponse> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/availability`, {
      method: 'POST',
      body: JSON.stringify({ date })
    });
    if (!response.ok) {
      return { available: false, reason: 'Error de disponibilidad' };
    }
    const result: AvailabilityResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error checking date availability:', error);
    return { available: false, reason: 'Error desconocido' };
  }
};

export const checkTimeAvailability = async (date: string, time: string): Promise<AvailabilityResponse> => {
  try {
    const response = await apiRequest(`${API_BASE_URL}/client/appointments/availability`, {
      method: 'POST',
      body: JSON.stringify({ date, time })
    });
    if (!response.ok) {
      return { available: false, reason: 'Error de disponibilidad' };
    }
    const result: AvailabilityResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error checking time availability:', error);
    return { available: false, reason: 'Error desconocido' };
  }
};
